// ─── Marketplace helpers: sorting, pools, activity, month vars, payout ────────
import {
  mkKey, MONTH_NAMES, getDaysInMonth, parseDate, toDateStr, fmtFull,
  isMonthKey,
} from "./utils.js";

// ─── Sort marketplaces (highest % of total sales first) ──────────────────────
// Previously pinned Amazon at the top regardless of actual volume. Users with
// Shopify-dominant or multi-channel operations had Amazon locked at the left
// of the carousel even when it contributed 5% of sales — confusing. Now the
// strongest contributor sits first; ties break by name for stable ordering.
export function sortMkts(arr) {
  return [...arr].sort((a, b) => {
    const sa = Number(a?.pctOfTotal) || 0;
    const sb = Number(b?.pctOfTotal) || 0;
    if (sa !== sb) return sb - sa;
    return (a?.name || "").localeCompare(b?.name || "");
  });
}

// ─── Year range helpers ───────────────────────────────────────────────────────
export function getYearRange(today) {
  const currentYear = today.getFullYear();
  return { currentYear, nextYear: currentYear + 1 };
}

export function buildInitialMonths(today) {
  const months = {};
  const { currentYear, nextYear } = getYearRange(today);
  for (const year of [currentYear - 1, currentYear, nextYear]) {
    for (let m = 1; m <= 12; m++) {
      months[mkKey(year, m)] = { target: 0, expenses: [] };
    }
  }
  return months;
}

// ─── Payout calculation for a marketplace ────────────────────────────────────
// Back-compat wrapper. When `state` and `window` are provided it delegates to
// the realized-payout calc which respects API-sourced net payouts and per-
// product sales windows. When not, it falls through to the legacy list-price
// × share formula so callers that don't have the full context keep working.
export function payoutForMkt(mkt, state, window) {
  if (state && window) {
    return realizedPayoutForMkt(mkt, state, window);
  }
  if (!mkt || !Array.isArray(mkt.products)) return 0;
  return mkt.products.reduce((s, p) => {
    const pct = Number(p?.pctOfMarket) || 0;
    const pay = Number(p?.payout) || 0;
    return s + pct * pay;
  }, 0);
}

// ─── Realized payout (per product + per marketplace) ─────────────────────────
// The seller's realized payout per unit = Σ net payouts in window ÷ units sold
// in same window, allocated to each product by its share of net revenue.
// Three tiers, each tagged on the returned object via `basis`:
//   "realized"   — marketplace received API-sourced payouts in the window
//   "net_revenue" — no payouts yet but we have order history (revenue − refunds)
//   "list_price" — no order history; fall back to product.payout
//
// Shopify Payments payouts are persisted as state.months[key].incomeItems[]
// with source="shopify_payments" + marketplaceId. Amazon/eBay/Walmart follow
// the same convention (see their respective sync endpoints). Any income item
// tagged with a marketplace id and a recognized source counts.

const PAYOUT_SOURCES = new Set(["shopify_payments", "amazon_payouts", "ebay_payouts", "walmart_payouts"]);

// Shopify Payments default settlement hold — orders sold today usually pay out
// ~3 days later. Shift the payout window back by this lag so a W-day payout
// total maps to the right W-day units total instead of leading by 3 days.
const DEFAULT_PAYOUT_LAG_DAYS = 3;

// Parse a "DD.MM.YYYY" date string → ms since epoch. Returns NaN on bad input.
function parseDdMmYyyy(s) {
  if (!s || typeof s !== "string") return NaN;
  const [dd, mm, yyyy] = s.split(".");
  if (!dd || !mm || !yyyy) return NaN;
  return new Date(`${yyyy}-${mm}-${dd}T00:00:00Z`).getTime();
}

// Sum every payout-type income item for `mkt` whose date falls in the window
// ending now and starting `windowDays + payoutLag` days ago. Returns a plain
// number (net cash, in shop currency).
export function sumMarketplacePayouts(mkt, state, windowDays, payoutLag = DEFAULT_PAYOUT_LAG_DAYS) {
  if (!mkt || !state?.months) return 0;
  const nowMs = Date.now();
  const startMs = nowMs - (windowDays + payoutLag) * 86400000;
  let total = 0;
  for (const m of Object.values(state.months)) {
    const items = m?.incomeItems;
    if (!Array.isArray(items)) continue;
    for (const it of items) {
      if (!it || it.marketplaceId !== mkt.id) continue;
      if (!PAYOUT_SOURCES.has(it.source)) continue;
      const t = parseDdMmYyyy(it.date);
      if (isNaN(t) || t < startMs || t > nowMs) continue;
      total += Number(it.amount) || 0;
    }
  }
  return total;
}

// Realized payout per product, for a given sales window (7/14/30/60/90 days).
// Mutates nothing; returns a Map<productClientId, { perUnit, basis, units }>.
//
// Callers that want a plain object can pass the map through Object.fromEntries.
export function realizedPayoutPerProduct(mkt, state, windowDays = 30) {
  const out = new Map();
  if (!mkt || !Array.isArray(mkt.products)) return out;
  const products = mkt.products;

  const mktNetPayout = sumMarketplacePayouts(mkt, state, windowDays);
  // Compute per-product window stats once.
  const stats = products.map(p => {
    const w = p?._salesWindows?.[windowDays] || p?._salesWindows?.[String(windowDays)] || null;
    const units = Number(w?.units) || 0;
    const revenue = Number(w?.revenue) || 0;
    const refunds = Number(w?.refunds) || 0;
    const netRevenue = Math.max(0, revenue - refunds);
    return { p, units, revenue, refunds, netRevenue };
  });

  const totalNetRevenue = stats.reduce((s, x) => s + x.netRevenue, 0);
  const totalUnits = stats.reduce((s, x) => s + x.units, 0);

  // Tier A — realized. Requires a non-zero payout AND non-zero allocation base.
  if (mktNetPayout > 0 && (totalNetRevenue > 0 || totalUnits > 0)) {
    for (const { p, units, netRevenue } of stats) {
      if (units <= 0) {
        out.set(p.id, { perUnit: 0, basis: "realized", units: 0 });
        continue;
      }
      const share = totalNetRevenue > 0
        ? netRevenue / totalNetRevenue
        : units / totalUnits;
      const productPayout = mktNetPayout * share;
      out.set(p.id, { perUnit: productPayout / units, basis: "realized", units });
    }
    return out;
  }

  // Tier B — net revenue per unit (no payouts yet but we have orders).
  if (totalNetRevenue > 0) {
    for (const { p, units, netRevenue } of stats) {
      if (units > 0) {
        out.set(p.id, { perUnit: netRevenue / units, basis: "net_revenue", units });
      } else {
        out.set(p.id, { perUnit: Number(p.payout) || 0, basis: "list_price", units: 0 });
      }
    }
    return out;
  }

  // Tier C — fall back to list price.
  for (const { p, units } of stats) {
    out.set(p.id, { perUnit: Number(p.payout) || 0, basis: "list_price", units });
  }
  return out;
}

// Realized payout averaged across the marketplace, weighted by pctOfMarket.
// Drop-in replacement for the legacy `payoutForMkt(mkt)`.
export function realizedPayoutForMkt(mkt, state, windowDays = 30) {
  if (!mkt || !Array.isArray(mkt.products)) return 0;
  const perProduct = realizedPayoutPerProduct(mkt, state, windowDays);
  return mkt.products.reduce((s, p) => {
    const pct = Number(p?.pctOfMarket) || 0;
    const rec = perProduct.get(p.id);
    const perUnit = rec ? rec.perUnit : (Number(p?.payout) || 0);
    return s + pct * perUnit;
  }, 0);
}

// ─── Audit helpers (backing the Marketplace Data Audit page) ─────────────────

// Total net payouts for `mkt` in the selected window. Same lag-shifted
// window the realized-payout cascade uses, so this number is always the
// "numerator" the cascade divides by units.
export function totalPayoutForMktInWindow(mkt, state, windowDays = 30) {
  return sumMarketplacePayouts(mkt, state, windowDays);
}

// List every payout incomeItem for `mkt` in the window, sorted newest-first.
// Rendered as a table in Section 3 of the audit page (so the user can see
// "3 payouts summing to $18,245.12" broken down by date + amount).
export function listPayoutsForMktInWindow(mkt, state, windowDays = 30, payoutLag = DEFAULT_PAYOUT_LAG_DAYS) {
  if (!mkt || !state?.months) return [];
  const nowMs = Date.now();
  const startMs = nowMs - (windowDays + payoutLag) * 86400000;
  const out = [];
  for (const m of Object.values(state.months)) {
    const items = m?.incomeItems;
    if (!Array.isArray(items)) continue;
    for (const it of items) {
      if (!it || it.marketplaceId !== mkt.id) continue;
      if (!PAYOUT_SOURCES.has(it.source)) continue;
      const t = parseDdMmYyyy(it.date);
      if (isNaN(t) || t < startMs || t > nowMs) continue;
      out.push({
        id: it.id,
        date: it.date,
        dateMs: t,
        amount: Number(it.amount) || 0,
        chargesFee: Number(it.chargesFee) || 0,
        refundsFee: Number(it.refundsFee) || 0,
        chargesGross: Number(it.chargesGross) || 0,
      });
    }
  }
  out.sort((a, b) => b.dateMs - a.dateMs);
  return out;
}

// Σ across all products of _salesWindows[W].lineItems — "products ordered".
// Distinct from orders (one order can contain multiple line items) and
// from units (a single line item can have qty > 1).
export function lineItemsInWindow(mkt, windowDays = 30) {
  if (!mkt || !Array.isArray(mkt.products)) return 0;
  return mkt.products.reduce((s, p) => {
    const w = p?._salesWindows?.[windowDays] || p?._salesWindows?.[String(windowDays)];
    return s + (Number(w?.lineItems) || 0);
  }, 0);
}

// Data-integrity checks surfaced as red banners on the audit page.
// Each entry = { severity: "warn" | "error", label, detail }.
// All purely derived — no state mutation.
export function integrityChecks(mkt, state, windowDays = 30) {
  const out = [];
  if (!mkt) return out;
  const products = mkt.products || [];

  // 1. unitsCount (stored scalar) vs Σ per-product window units
  const storedUnits = Number(mkt.unitsCount ?? 0);
  const computedUnits = products.reduce((s, p) => {
    const w = p?._salesWindows?.[windowDays] || p?._salesWindows?.[String(windowDays)];
    return s + (Number(w?.units) || 0);
  }, 0);
  if (mkt.unitsCount != null && Math.abs(storedUnits - computedUnits) > 1) {
    out.push({
      severity: "warn",
      label: "Stored units count is stale",
      detail: `The marketplace row stores ${storedUnits.toLocaleString()} units for the default window. Summing each product's ${windowDays}-day window gives ${computedUnits.toLocaleString()}. Re-sync to refresh.`,
    });
  }

  // 2. Products with pctOfMarket > 0 but empty _salesWindows
  const ghostProducts = products.filter(p => !p._decoupled && (Number(p.pctOfMarket) || 0) > 0 && (!p._salesWindows || Object.keys(p._salesWindows).length === 0));
  if (ghostProducts.length > 0) {
    out.push({
      severity: "warn",
      label: `${ghostProducts.length} product(s) have a share but no sales history`,
      detail: `These products will contribute 0% on the next Recompute: ${ghostProducts.slice(0, 5).map(p => p.name).join(", ")}${ghostProducts.length > 5 ? "…" : ""}.`,
    });
  }

  // 3. Σ pctOfMarket ≠ 1.0 (±0.5%)
  const sumShare = products.filter(p => !p._decoupled).reduce((s, p) => s + (Number(p.pctOfMarket) || 0), 0);
  if (products.length > 0 && Math.abs(sumShare - 1) > 0.005) {
    out.push({
      severity: "warn",
      label: `Product shares don't sum to 100%`,
      detail: `Current Σ pctOfMarket = ${(sumShare * 100).toFixed(2)}% (expected 100%). Click "Recompute shares" or adjust manual overrides so they total 100%.`,
    });
  }

  // 4. Revenue > 0 in window but payouts = 0
  const winRev = products.reduce((s, p) => {
    const w = p?._salesWindows?.[windowDays] || p?._salesWindows?.[String(windowDays)];
    return s + (Number(w?.revenue) || 0);
  }, 0);
  const winPayouts = sumMarketplacePayouts(mkt, state, windowDays);
  if (winRev > 0 && winPayouts === 0) {
    out.push({
      severity: "warn",
      label: "Orders exist but no payouts yet",
      detail: `Seeing ${winRev.toFixed(2)} in revenue for the window but no payouts have landed in CashHorizon. Either payouts haven't arrived yet (typical 2-3 days for Shopify Payments), the store uses a non-Shopify gateway, or payouts sync is disabled.`,
    });
  }

  // 5. List payout and realized diverge by >15%
  const blendedList = products.reduce((s, p) => s + (Number(p.pctOfMarket) || 0) * (Number(p.payout) || 0), 0);
  const blendedReal = realizedPayoutForMkt(mkt, state, windowDays);
  if (blendedList > 0 && blendedReal > 0) {
    const delta = Math.abs(blendedList - blendedReal) / blendedList;
    if (delta > 0.15) {
      out.push({
        severity: "warn",
        label: "List price and realized payout differ by >15%",
        detail: `Blended list: $${blendedList.toFixed(2)}/unit · Blended realized: $${blendedReal.toFixed(2)}/unit. Most sellers see 3-5% gap (Shopify fees). A 15%+ gap usually means an un-mapped fee, heavy discounting, or tax mis-attribution.`,
      });
    }
  }

  return out;
}

// ─── Inventory Pool helpers ──────────────────────────────────────────────────
export function getPoolForMarketplace(pools, mktId) {
  if (!pools || !pools.length) return null;
  return pools.find(p => (p.marketplaceIds || []).includes(mktId)) || pools.find(p => !(p.marketplaceIds || []).length) || null;
}

export function getEffectivePool(pools, marketplaceIds) {
  if (!pools || !pools.length) return null;
  if (!marketplaceIds || !marketplaceIds.length) {
    return pools.find(p => !(p.marketplaceIds || []).length) || pools[0];
  }
  return getPoolForMarketplace(pools, marketplaceIds[0]);
}

// ─── Synthetic aggregate pool for multi-marketplace selection ────────────────
// Returns a single pool object representing the union of every pool whose
// marketplaceIds intersect the selection. Used by Dashboard + Targets to drive
// inventory KPIs in multi-mkt and "all" mode without defaulting to "first
// pool" (the bug that made every page show only Amazon when "All" was
// selected). Always returns a pool when at least one matching pool exists;
// returns null only when there are no pools at all.
//
// When exactly one pool matches, it's returned as-is (no synthesis needed).
// When multiple pools match, the result is a synthetic pool with:
//   - initialStock summed across contributors
//   - apiStockSnapshot summed across contributors
//   - initialCostPerUnit = mass-weighted average
//   - initialStockDate = the latest among contributors
//   - marketplaceIds + orderIds = the union
//   - stockOverridden = true only if EVERY contributing pool has it set
//   - _synthetic: true so the UI can disable per-pool affordances
export function synthesizePoolForSelection(pools, selectedMktIds, marketplaces) {
  if (!pools || !pools.length) return null;
  const contributing = (selectedMktIds && selectedMktIds.length)
    ? pools.filter(p => (p.marketplaceIds || []).some(id => selectedMktIds.includes(id)))
    : pools;
  if (contributing.length === 0) return null;
  if (contributing.length === 1) return contributing[0];
  const allShared = contributing.every(p => p.isShared);
  const sumStock = contributing.reduce((s, p) => s + (Number(p.initialStock) || 0), 0);
  const sumApiSnapshot = contributing.reduce((s, p) => s + (Number(p.apiStockSnapshot) || 0), 0);
  // Cost-per-unit: use the FULL COG hierarchy per pool (pool > mkt.avgCogs >
  // group.cogs > product.cogs) instead of just pool.initialCostPerUnit.
  // Without this, marketplace-level avgCogs overrides are invisible because
  // the synthetic pool's initialCostPerUnit is computed from the raw pool
  // values that don't reflect mkt.avgCogs. The fix: for each pool, check if
  // its own cost is set; if not, fall back to the per-marketplace cascade.
  const totalCostValue = contributing.reduce((s, p) => {
    const stock = Number(p.initialStock) || 0;
    if (stock <= 0) return s;
    // Pool's own cost takes priority
    let costPerUnit = Number(p.initialCostPerUnit) || 0;
    if (costPerUnit <= 0 && Array.isArray(marketplaces)) {
      // Fall through to per-marketplace avgCogs → group → product (inline cascade)
      const poolMktIds = new Set(p.marketplaceIds || []);
      let mktCostSum = 0, mktWeightSum = 0;
      for (const mkt of marketplaces) {
        if (!poolMktIds.has(mkt.id)) continue;
        const w = Number(mkt.pctOfTotal) || 0;
        const mktCog = Number(mkt.avgCogs) || 0;
        if (mktCog > 0) { mktCostSum += mktCog * w; mktWeightSum += w; continue; }
        // Per-product fallback
        let prodCostSum = 0, prodWeightSum = 0;
        for (const pr of mkt.products || []) {
          if (pr._decoupled) continue;
          const pw = Number(pr.pctOfMarket) || 0;
          if (pw <= 0) continue;
          const g = (mkt.productGroups || []).find(gr => (gr.productIds || []).includes(pr.id));
          const gc = Number(g?.cogs) || 0;
          if (gc > 0) { prodCostSum += gc * pw; prodWeightSum += pw; continue; }
          const c = pr.cogs || {};
          const pc = (Number(c.productCost)||0)+(Number(c.shipping)||0)+(Number(c.customs)||0)+(Number(c.prep)||0);
          if (pc > 0) { prodCostSum += pc * pw; prodWeightSum += pw; }
        }
        if (prodWeightSum > 0) { mktCostSum += (prodCostSum / prodWeightSum) * w; mktWeightSum += w; }
      }
      if (mktWeightSum > 0) costPerUnit = mktCostSum / mktWeightSum;
    }
    return s + stock * costPerUnit;
  }, 0);
  const weightedCost = sumStock > 0 ? totalCostValue / sumStock : 0;
  const latestDate = contributing.reduce((latest, p) => {
    if (!p.initialStockDate) return latest;
    return !latest || p.initialStockDate > latest ? p.initialStockDate : latest;
  }, null);
  const unionMktIds = [...new Set(contributing.flatMap(p => p.marketplaceIds || []))];
  const unionOrderIds = [...new Set(contributing.flatMap(p => p.orderIds || []))];
  return {
    id: "_synthetic_pool",
    name: contributing.length === pools.length ? "All pools" : `${contributing.length} pools`,
    marketplaceIds: unionMktIds,
    orderIds: unionOrderIds,
    initialStock: sumStock,
    initialStockDate: latestDate,
    initialCostPerUnit: weightedCost,
    stockOverridden: contributing.every(p => p.stockOverridden),
    apiStockSnapshot: sumApiSnapshot,
    isShared: allShared,
    _synthetic: true,
    _contributingPoolCount: contributing.length,
  };
}

export function getOrdersForPool(orders, pool) {
  if (!pool) return orders || [];
  if (pool.orderIds && pool.orderIds.length) {
    return (orders || []).filter(o => pool.orderIds.includes(o.id));
  }
  if (pool.marketplaceIds && pool.marketplaceIds.length) {
    // Untagged orders no longer auto-pollute marketplace-scoped pools. After
    // the order-marketplace tag became mandatory (orders form validation),
    // every new order must declare its marketplace(s); only LEGACY untagged
    // orders fall through to here. Treat them as belonging to a "global"
    // pool only — they don't contribute to per-marketplace inventory.
    return (orders || []).filter(o => {
      if (!o.marketplaceIds || !o.marketplaceIds.length) return false;
      return o.marketplaceIds.some(mid => pool.marketplaceIds.includes(mid));
    });
  }
  return orders || [];
}

// ─── Check if marketplace/product is active for a given month key ────────────
export function isMktActiveForKey(mkt, key) {
  if (!mkt || !mkt.activeFrom) return true;
  return key >= mkt.activeFrom;
}
export function isProdActiveForKey(prod, key) {
  if (!prod) return true;
  // Phase 5 — sandbox products are a private scratchpad; they never
  // contribute income to any month. Costs are also irrelevant here
  // because this helper only gates the income side of the engine.
  if (prod.sandbox) return false;
  // launchDate is "YYYY-MM-DD" and represents the day income starts. Costs
  // are NOT gated by it (per Products page Phase 4) — only the income-side
  // calls go through this helper. Truncate to YYYY-MM so the comparison
  // against the month key works at the same granularity as the rest of
  // the engine. Falls back to activeFrom for products that don't have a
  // launchDate set yet.
  const ld = prod.launchDate ? String(prod.launchDate).slice(0, 7) : null;
  const from = ld || prod.activeFrom;
  if (!from) return true;
  return key >= from;
}

// ─── Get active marketplaces for a month key ──────────────────────────────────
export function getActiveMarketplaces(marketplaces, key) {
  if (!Array.isArray(marketplaces)) return [];
  return marketplaces.filter(m => m && isMktActiveForKey(m, key)).map(m => ({
    ...m,
    products: Array.isArray(m.products) ? m.products.filter(p => p && isProdActiveForKey(p, key)) : [],
  }));
}

// ─── Get effective month variables (global + per-month overrides) ─────────────
export function getMonthVars(key, marketplaces, months) {
  const mv = (months && months[key]?.vars) || {};
  const activeMkts = getActiveMarketplaces(marketplaces, key);
  return {
    mv,
    marketplaces: activeMkts.map(mkt => {
      const mktPayoutMod = Number(mv[`payoutmod_${mkt.id}`]) || 0;
      return {
        ...mkt,
        pctOfTotal: Number(mv[`pct_${mkt.id}`] ?? mkt.pctOfTotal) || 0,
        payoutDelay: Number(mv[`delay_${mkt.id}`] ?? mkt.payoutDelay) || 0,
        payoutMod: mktPayoutMod,
        products: (mkt.products || []).map(p => {
          const basePayout = Number(mv[`payout_${mkt.id}_${p.id}`] ?? p.payout) || 0;
          const prodMod = Number(mv[`payoutmod_${mkt.id}_${p.id}`]) || 0;
          const totalMod = mktPayoutMod + prodMod;
          return {
            ...p,
            pctOfMarket: Number(mv[`pct_${mkt.id}_${p.id}`] ?? p.pctOfMarket) || 0,
            payout: basePayout * (1 + totalMod),
            deliveryCost: Number(mv[`deliv_${mkt.id}_${p.id}`] ?? p.deliveryCost) || 0,
            payoutMod: totalMod,
          };
        }),
      };
    }),
  };
}

// ─── Effective ad cost per unit (hierarchy: product → group → marketplace) ──
// Returns the dollar amount of advertising spend for ONE unit of this product,
// based on the most specific source available. None of the marketplace APIs
// (Shopify, Amazon SP-API, Walmart, eBay) return ad spend, so this is always
// user-input. The hierarchy lets the user pick the level they want to manage at:
//
//   1. Product-level (most specific)  — p.adCostRate / p.adCostAmount
//   2. Group-level                     — group.adCostRate / group.adCostAmount
//      (the group containing this product on the marketplace)
//   3. Marketplace-level                — mkt.adCostRate / mkt.adCostAmount
//   4. Zero
//
// Product-level always wins if it has a non-zero value. The deduct toggle
// (deductAdCostFromProjection) only gates the PRODUCT-level value — group
// and marketplace fallbacks are always applied when set, since the user
// explicitly entered them at that level.
export function effectiveAdCostPerUnit(product, mkt, group) {
  const payout = Number(product?.payout) || 0;
  // 1. Product level — only if the user opted in via deductAdCostFromProjection
  if (product?.deductAdCostFromProjection) {
    const t = product.adCostType || (product.ppcType === "fixed" ? "fixed" : "pct");
    if (t === "pct") {
      let rate = Number(product.adCostRate ?? product.ppcRate) || 0;
      if (rate > 0) {
        if (rate > 1) rate = 1;
        return payout * rate;
      }
    } else {
      const amt = Number(product.adCostAmount) || 0;
      if (amt > 0) return amt;
    }
  }
  // 2. Group level — applies whenever set, no opt-in toggle
  if (group) {
    const t = group.adCostType || "pct";
    if (t === "pct") {
      let rate = Number(group.adCostRate) || 0;
      if (rate > 0) {
        if (rate > 1) rate = 1;
        return payout * rate;
      }
    } else {
      const amt = Number(group.adCostAmount) || 0;
      if (amt > 0) return amt;
    }
  }
  // 3. Marketplace level — same: applies whenever set
  if (mkt) {
    const t = mkt.adCostType || "pct";
    if (t === "pct") {
      let rate = Number(mkt.adCostRate) || 0;
      if (rate > 0) {
        if (rate > 1) rate = 1;
        return payout * rate;
      }
    } else {
      const amt = Number(mkt.adCostAmount) || 0;
      if (amt > 0) return amt;
    }
  }
  return 0;
}

// Find the group on a marketplace that contains this product (or null).
function findGroupForProduct(mkt, productId) {
  const groups = mkt?.productGroups || [];
  return groups.find(g => Array.isArray(g.productIds) && g.productIds.includes(productId)) || null;
}

// ─── Effective payout per unit (deducts advertising cost if opted in) ────────
// When `state` and `windowDays` are supplied, the base payout per unit comes
// from the realized cascade (API payouts → net revenue → list price) instead
// of the raw product.payout list price. Callers that don't pass state keep the
// legacy behavior unchanged — important for places like tests and kpiSnapshot
// that expect the old semantics.
export function effectivePayoutForMkt(mkt, state, windowDays) {
  if (!mkt || !Array.isArray(mkt.products)) return 0;
  const realizedByProd = (state && windowDays)
    ? realizedPayoutPerProduct(mkt, state, windowDays)
    : null;
  return mkt.products.reduce((s, p) => {
    // Decoupled products are excluded from EVERY calculation
    if (p?._decoupled) return s;
    const pct = Number(p?.pctOfMarket) || 0;
    const realized = realizedByProd?.get(p.id);
    let pay = realized && realized.perUnit > 0
      ? realized.perUnit
      : Number(p?.payout) || 0;
    // Delivery / fulfillment cost — only if user opted to deduct from payout
    if (p?.deductDeliveryFromPayout) {
      const dlv = Number(p?.deliveryCost) || 0;
      if (dlv > 0) pay = pay - dlv;
    }
    // Ad cost — uses the product → group → marketplace hierarchy
    const group = findGroupForProduct(mkt, p?.id);
    const adDed = effectiveAdCostPerUnit(p, mkt, group);
    if (adDed > 0) pay = pay - adDed;
    // Refunds — same opt-in toggle as before (per-product only for now).
    //
    // Bug guard: the naive `pay = pay * (1 - rate)` formula flips direction
    // when `pay` is already negative (e.g., after ad cost exceeds payout on
    // a loss-making SKU). A refund should never make a loss smaller — it
    // costs additional money. We always treat refund as a subtraction
    // from `pay`, using the positive portion of `pay` as the base for
    // the pct calculation so a loss-making product stays loss-making
    // after refunds.
    const deductRefund = p?.deductRefundFromProjection ?? p?.deductAdCostFromProjection;
    if (deductRefund) {
      const refType = p.refundType || "pct";
      if (refType === "pct") {
        const base = Math.max(0, pay);
        const refundCost = base * (Number(p.refundRate) || 0);
        pay = pay - refundCost;
      } else {
        pay = pay - (Number(p.refundAmount) || 0);
      }
    }
    return s + pct * Math.max(0, pay);
  }, 0);
}

// ─── Effective units for a month ──────────────────────────────────────────────
export function effectiveUnits(key, months, today) {
  const mData = months[key] || { target: 0 };
  const todayStr = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
  if (mData.unitsOverrideDate === todayStr && mData.unitsOverride != null) return mData.unitsOverride;
  const [yr, mo] = key.split("-").map(Number);
  const dim = getDaysInMonth(yr, mo);
  const isCurrent = yr === today.getFullYear() && mo === today.getMonth() + 1;
  const isPast = yr < today.getFullYear() || (yr === today.getFullYear() && mo < today.getMonth() + 1);

  if (mData.useActualSold && mData.actualSold > 0) {
    if (isPast) return mData.actualSold;
    if (isCurrent) {
      const daysSoFar = today.getDate();
      const runRate = mData.actualSold / Math.max(1, daysSoFar);
      const daysLeft = dim - daysSoFar;
      return Math.round(runRate * daysLeft);
    }
  }

  let auto;
  if (isCurrent) auto = Math.round((mData.target || 0) * ((dim - today.getDate()) / dim));
  else if (isPast) auto = 0;
  else auto = (mData.target || 0);
  if (mData.unitsOverrideDelta != null && isCurrent) {
    return Math.max(0, auto + mData.unitsOverrideDelta);
  }
  return auto;
}

// ─── Get monthly payout breakdown per marketplace ─────────────────────────────
// Returns one row per marketplace with a `products` array. Products that
// belong to a productGroup on their marketplace are FOLDED into a single
// synthetic group row (units/payout summed across children) — the children
// themselves are hidden from the breakdown so the Cash Calendar shows just
// the parent. Ungrouped products appear as individual rows like before.
export function getMonthlyPayoutBreakdown(key, marketplaces, months, modifiedMkts) {
  const mv = getMonthVars(key, marketplaces, months);
  const [yr, mo] = key.split("-").map(Number);
  const target = months[key]?.target || 0;
  // If caller passes pre-modified marketplaces (with modifiers applied),
  // use those for the payout calculation so Cash Calendar matches Dashboard.
  const mktsToUse = modifiedMkts || mv.marketplaces;
  const result = [];
  // Pass { months } so per-marketplace payout runs through the realized
  // cascade (API payouts → net revenue → list price) — keeps the Cash
  // Calendar's per-marketplace rows in sync with the Dashboard income chart.
  const payoutCtx = { months };
  for (const mkt of mktsToUse) {
    const mktUnits = Math.round(target * mkt.pctOfTotal);
    const mktPayout = mktUnits * effectivePayoutForMkt(mkt, payoutCtx, 30);
    const delay = mkt.payoutDelay || 0;
    // Compute per-product rows first (ad cost + refund hierarchy applied)
    const perProductRows = mkt.products.map(p => {
      const pUnits = Math.round(mktUnits * p.pctOfMarket);
      let effectivePay = p.payout;
      const group = findGroupForProduct(mkt, p.id);
      const adDed = effectiveAdCostPerUnit(p, mkt, group);
      if (adDed > 0) effectivePay -= adDed;
      if (p.deductRefundFromProjection ?? p.deductAdCostFromProjection) {
        const refType = p.refundType || "pct";
        if (refType === "pct") effectivePay *= (1 - (Number(p.refundRate) || 0));
        else effectivePay -= (Number(p.refundAmount) || 0);
      }
      effectivePay = Math.max(0, effectivePay);
      return {
        prodId: p.id,
        prodName: p.name,
        units: pUnits,
        payout: pUnits * effectivePay,
        grossPayout: pUnits * p.payout,
        _groupId: group?.id || null,
      };
    });
    // Fold grouped product rows into synthetic parent rows. Ungrouped rows
    // pass through unchanged. Order: groups first (by name), then ungrouped.
    const groups = mkt.productGroups || [];
    const productRowsByGroupId = {};
    const ungroupedRows = [];
    for (const row of perProductRows) {
      if (row._groupId) {
        (productRowsByGroupId[row._groupId] = productRowsByGroupId[row._groupId] || []).push(row);
      } else {
        ungroupedRows.push(row);
      }
    }
    const groupRows = groups
      .filter(g => productRowsByGroupId[g.id]?.length)
      .map(g => {
        const rows = productRowsByGroupId[g.id];
        const sumUnits  = rows.reduce((s, r) => s + r.units, 0);
        const sumPayout = rows.reduce((s, r) => s + r.payout, 0);
        const sumGross  = rows.reduce((s, r) => s + r.grossPayout, 0);
        return {
          prodId: g.id,
          prodName: `👪 ${g.name}`,  // visual hint that it's a group
          units: sumUnits,
          payout: sumPayout,
          grossPayout: sumGross,
          _isGroup: true,
          _childCount: rows.length,
        };
      });
    // Strip the internal _groupId scratch field from passed-through rows
    const cleanedUngrouped = ungroupedRows.map(({ _groupId, ...rest }) => rest);
    result.push({
      mktId: mkt.id,
      mktName: mkt.name,
      units: mktUnits,
      payout: mktPayout,
      grossPayout: mktUnits * payoutForMkt(mkt, payoutCtx, 30),
      payoutDelay: delay,
      products: [...groupRows, ...cleanedUngrouped],
    });
  }
  return result;
}

// ─── Recurring summary label ──────────────────────────────────────────────────
export function recurringItemSummary(item) {
  const amt = fmtFull(item.amount || 0);
  if (item.type === "simple") {
    return `${amt} every ${item.intervalDays || 30} days`;
  }
  if (item.type === "units") {
    return `${amt} every ${item.perUnits || 100} units sold`;
  }
  const months = (item.activeMonths || [1,2,3,4,5,6,7,8,9,10,11,12]);
  if (months.length === 12) return `${amt} every month`;
  const monthLabels = months.map(m => MONTH_NAMES[m - 1]).join(", ");
  return `${amt} in ${monthLabels}`;
}

// ─── Get overdue unpaid expenses ──────────────────────────────────────────────
export function getOverdueExpenses(months, deliveryExpenses, orderExpenses, recurringItems, today) {
  const result = [];
  const todayTime = today.getTime();

  for (const [key, mData] of Object.entries(months)) {
    if (!isMonthKey(key)) continue;
    for (const exp of (mData.expenses || [])) {
      if (exp.paid) continue;
      let amt = exp.amount || 0;
      if (exp.partialAmount !== undefined) amt = Math.max(0, amt - exp.partialAmount);
      if (amt <= 0) continue;
      const d = parseDate(exp.date);
      if (d && d.getTime() < todayTime) {
        result.push({ id: `manual_${exp.id}`, label: exp.what || "✏️ Manual expense", amount: amt, date: exp.date, type: "manual", mKey: key, expId: exp.id });
      }
    }
  }

  for (const [key, exps] of Object.entries(deliveryExpenses)) {
    const mData = months[key] || {};
    for (const exp of exps) {
      const dovr = (mData.deliveryOverrides || {})[exp.id] || {};
      if (dovr.paid || dovr.deleted) continue;
      let amt = exp.amount || 0;
      if (dovr.partialAmount !== undefined) amt = Math.max(0, amt - dovr.partialAmount);
      if (amt <= 0) continue;
      const ds = dovr.delayedDate || dovr.date || exp.date;
      const d = parseDate(ds);
      if (d && d.getTime() < todayTime) {
        result.push({ id: `delivery_${exp.id}`, label: exp.what || "📦 Fulfilment", amount: amt, date: ds, type: "delivery", mKey: key, expId: exp.id });
      }
    }
  }

  for (const [key, exps] of Object.entries(orderExpenses)) {
    const mData = months[key] || {};
    for (const exp of exps) {
      if ((mData.orderExpPaid || {})[exp.id]) continue;
      let amt = exp.amount || 0;
      const amountPaid = (mData.orderExpPartial || {})[exp.id];
      if (amountPaid !== undefined) amt = Math.max(0, amt - amountPaid);
      if (amt <= 0) continue;
      const delayed = (mData.orderExpDelayed || {})[exp.id];
      const ds = (delayed && delayed.newDate) ? delayed.newDate : exp.date;
      const d = parseDate(ds);
      if (d && d.getTime() < todayTime) {
        result.push({ id: `order_${exp.id}`, label: exp.what || "💳 Order payment", amount: amt, date: ds, type: "order", mKey: key, expId: exp.id });
      }
    }
  }

  for (const [key, items] of Object.entries(recurringItems)) {
    const mData = months[key] || {};
    for (const item of items) {
      if (item.isIncome) continue;
      const ov = (mData.recurringOverrides || {})[item.id||item.recurringId] || {};
      if (ov.paid || ov.deleted) continue;
      let amt = ov.amount !== undefined ? ov.amount : (item.amount || 0);
      if (ov.partialAmount !== undefined) amt = Math.max(0, amt - ov.partialAmount);
      if (amt <= 0) continue;
      const ds = ov.delayedDate || item.date;
      const d = parseDate(ds);
      if (d && d.getTime() < todayTime) {
        result.push({ id: `rec_${item.id}`, label: item.what || "🔄 Recurring", amount: amt, date: ds, type: "recurring", mKey: key, recurringId: item.recurringId });
      }
    }
  }

  return result.sort((a, b) => {
    const da = parseDate(a.date), db = parseDate(b.date);
    return (da?.getTime() || 0) - (db?.getTime() || 0);
  });
}

// ─── App-wide error/warning detection ────────────────────────────────────────
export function detectAppIssues(marketplaces, months, orders, visibleYears) {
  const issues = [];

  for (const year of visibleYears) {
    for (let m = 1; m <= 12; m++) {
      const key = mkKey(year, m);
      const activeMkts = getActiveMarketplaces(marketplaces, key);
      const total = activeMkts.reduce((s, mkt) => s + (mkt.pctOfTotal || 0), 0);
      if (activeMkts.length > 0 && Math.abs(total - 1) > 0.01) {
        issues.push({
          id: `mkt_pct_${key}`, severity: "error", page: "marketplaces",
          label: `Marketplace totals ${Math.round(total * 100)}% (not 100%) in ${MONTH_NAMES[m-1]} ${year} — ${activeMkts.map(mk=>mk.name).join(", ")}`,
          detail: `Current total: ${Math.round(total * 100)}% across: ${activeMkts.map(mk=>mk.name+"="+Math.round((mk.pctOfTotal||0)*100)+"%").join(", ")}. Adjust to sum to 100%.`,
          navPage: "marketplaces", navHighlight: "pct_total",
        });
        break;
      }
    }
  }

  const globalTotal = marketplaces.reduce((s, mkt) => s + (mkt.pctOfTotal || 0), 0);
  if (marketplaces.length > 0 && Math.abs(globalTotal - 1) > 0.01) {
    issues.push({
      id: "mkt_pct_global", severity: "error", page: "marketplaces",
      label: `Global % total ${Math.round(globalTotal * 100)}% — ${marketplaces.map(mk=>mk.name+" "+Math.round((mk.pctOfTotal||0)*100)+"%").join(", ")} — must equal 100%`,
      detail: `Adjust marketplace % splits so they sum to 100%. Click to navigate.`,
      navPage: "marketplaces", navHighlight: "pct_total",
    });
  }

  for (const order of orders) {
    const ptSum = (order.paymentTerms || []).reduce((s, p) => s + (p.pct || 0), 0);
    if (Math.abs(ptSum - 100) > 0.01) {
      issues.push({
        id: `order_pt_${order.id}`, severity: "error", page: "orders",
        label: `Order ${order.number}: payment terms sum to ${ptSum}% (should be 100%)`,
        detail: `Click to open order ${order.number} and fix payment terms.`,
        navPage: "orders", navHighlight: order.id,
      });
    }

    const totalCt = (order.containerLines || []).reduce((s, l) => s + (l.qty || 0), 0) || (order.totalContainers || 0);
    const assigned = (order.containerBatches || []).reduce((s, b) => s + (b.containers || 0), 0);
    if (totalCt > 0 && assigned < totalCt) {
      issues.push({
        id: `order_ct_${order.id}`, severity: "warning", page: "orders",
        label: `Order ${order.number}: ${totalCt - assigned} container(s) not scheduled in cashflow`,
        detail: `Click to open order ${order.number} and add container batches.`,
        navPage: "orders", navHighlight: order.id,
      });
    }
  }

  for (const mkt of marketplaces) {
    const prodTotal = mkt.products.reduce((s, p) => s + (p.pctOfMarket || 0), 0);
    if (mkt.products.length > 0 && Math.abs(prodTotal - 1) > 0.01) {
      issues.push({
        id: `prod_pct_${mkt.id}`, severity: "error", page: "marketplaces",
        label: `${mkt.name}: product splits don't add up to 100%`,
        detail: `Current: ${Math.round(prodTotal * 100)}% — click to fix ${mkt.name} products.`,
        navPage: "marketplaces", navHighlight: mkt.id,
      });
    }
  }

  // Warning: marketplace has stock but 0% share (ghost inventory)
  for (const mkt of marketplaces) {
    if ((mkt.pctOfTotal || 0) <= 0) {
      const hasStock = (mkt.products || []).some(p => (Number(p.currentStock) || Number(p.apiStock) || 0) > 0)
        || Number(mkt.currentStockOverride) > 0 || Number(mkt.lastSyncStock) > 0;
      if (hasStock) {
        issues.push({
          id: `ghost_stock_${mkt.id}`, severity: "warning", page: "marketplaces",
          label: `${mkt.name} has inventory but 0% revenue share — stock won't deplete in projections`,
          detail: `Set % Total on ${mkt.name} or click Recompute to distribute shares from sales data.`,
          navPage: "marketplaces", navHighlight: mkt.id,
        });
      }
    }
  }

  // Warning: orders with batches missing arrival dates
  for (const order of orders) {
    const missing = (order.containerBatches || []).filter(b => !b.arrivalDate);
    if (missing.length > 0) {
      issues.push({
        id: `order_noarr_${order.id}`, severity: "warning", page: "orders",
        label: `Order ${order.number}: ${missing.length} batch(es) without arrival dates — stock projections can't include these units`,
        detail: `Set arrival dates on ${order.number} so stock projections account for incoming inventory.`,
        navPage: "orders", navHighlight: order.id,
      });
    }
  }

  // Warning: orders not linked to any marketplace
  for (const order of orders) {
    if (!order.marketplaceIds || order.marketplaceIds.length === 0) {
      issues.push({
        id: `order_nomkt_${order.id}`, severity: "warning", page: "orders",
        label: `Order ${order.number} is not linked to any marketplace`,
        detail: `Link ${order.number} to a marketplace so stock projections and lead time calculations include it.`,
        navPage: "orders", navHighlight: order.id,
      });
    }
  }

  return issues;
}
